import { useState, useCallback } from "react";
import {
  getWallets,
  createWallet,
  updateWallet,
  getWalletSummary,
  addBalanceToWallet,
  getWalletTransfers,
  createWalletTransfer,
  getMyTransactions,
  getTransactionStats,
} from "../../../../api/marketplace";

export function useWallet() {
  const [wallets, setWallets] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchWallets = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const res = await getWallets(params);
      setWallets(res.data);
    } catch (err) {
      setError(err.response?.data || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await getWalletSummary();
      setSummary(res.data);
    } catch (err) {
      console.error("Failed to fetch wallet summary:", err);
    }
  }, []);

  const fetchTransfers = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const res = await getWalletTransfers(params);
      setTransfers(res.data);
    } catch (err) {
      setError(err.response?.data || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMyTransactions = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const res = await getMyTransactions(params);
      setTransactions(res.data.transactions || []);
      setSummary(res.data);
    } catch (err) {
      setError(err.response?.data || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const res = await getTransactionStats();
      setStats(res.data);
    } catch (err) {
      console.error("Failed to fetch transaction stats:", err);
    }
  }, []);

  const create = useCallback(async (data) => {
    const res = await createWallet(data);
    await fetchWallets();
    return res.data;
  }, [fetchWallets]);

  const update = useCallback(async (id, data) => {
    const res = await updateWallet(id, data);
    await fetchWallets();
    return res.data;
  }, [fetchWallets]);

  const addBalance = useCallback(async (id, data) => {
    const res = await addBalanceToWallet(id, data);
    await fetchWallets();
    return res.data;
  }, [fetchWallets]);

  const transfer = useCallback(async (data) => {
    const res = await createWalletTransfer(data);
    await fetchTransfers();
    return res.data;
  }, [fetchTransfers]);

  return {
    wallets,
    transfers,
    transactions,
    summary,
    stats,
    loading,
    error,
    fetchWallets,
    fetchSummary,
    fetchTransfers,
    fetchMyTransactions,
    fetchStats,
    create,
    update,
    addBalance,
    transfer,
  };
}
